import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  Rating,
  Chip,
  Stack,
  Skeleton,
} from '@mui/material';
import { Add as AddIcon, Event as EventIcon, WbSunny as WeatherIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { outfitAPI } from '../utils/api';
import { format } from 'date-fns';

const OutfitHistory = () => {
  const [outfits, setOutfits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to construct proper image URLs for both dev and production
  const getImageSrc = (src) => {
    if (!src) return null;
    
    // If it's an external URL (OpenAI, placeholders, etc.), return as-is
    if (src.includes('http') && !src.includes(window.location.hostname)) {
      return src;
    }
    
    // If it's an internal /api/images/ or /api/uploads/ URL, construct full backend URL
    if (src.startsWith('/api/images/') || src.startsWith('/api/uploads/')) {
      const token = localStorage.getItem('auth_token');
      
      // Get backend URL from environment or default to localhost
      let backendUrl;
      if (process.env.REACT_APP_API_URL) {
        backendUrl = process.env.REACT_APP_API_URL;
      } else if (process.env.NEXT_PUBLIC_API_URL) {
        backendUrl = process.env.NEXT_PUBLIC_API_URL;
      } else {
        backendUrl = 'http://localhost:5001';
      }
      
      // Construct full URL with backend domain
      const fullUrl = `${backendUrl}${src}`;
      
      if (token) {
        // Add token as query parameter for authentication
        const separator = fullUrl.includes('?') ? '&' : '?';
        return `${fullUrl}${separator}token=${encodeURIComponent(token)}`;
      }
      
      return fullUrl;
    }
    
    return src;
  };

  useEffect(() => {
    fetchOutfits();
  }, []);

  const fetchOutfits = async () => {
    try {
      setLoading(true);
      const response = await outfitAPI.getHistory();
      // Backend returns { success: true, data: [...], message: "..." }
      // So we need to access response.data.data
      setOutfits(response.data?.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching outfit history:', err);
      setError('Failed to load outfit history');
    } finally {
      setLoading(false);
    }
  };

  const renderOutfitCard = (outfit) => (
    <Card
      key={outfit.id}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 16px 28px rgba(15, 29, 55, 0.1)',
          borderColor: '#bfd2f1',
        },
      }}
    >
      {outfit.generated_image_url && (
        <CardMedia
          component="img"
          height="200"
          image={getImageSrc(outfit.generated_image_url)}
          alt={`Outfit worn on ${format(new Date(outfit.date_worn), 'PPP')}`}
          sx={{ objectFit: 'cover' }}
        />
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom>
          {format(new Date(outfit.date_worn), 'PPP')}
        </Typography>
        
        <Stack direction="row" spacing={1} mb={2}>
          {outfit.occasion && (
            <Chip
              icon={<EventIcon />}
              label={outfit.occasion}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {outfit.weather && (
            <Chip
              icon={<WeatherIcon />}
              label={outfit.weather}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Stack>

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Items:
        </Typography>
        <Stack spacing={1} mb={2}>
          {(outfit.items || []).map((item) => (
            <Typography key={item.id} variant="body2">
              • {item.closet_item?.name || item.closet_item?.description || 'Unknown Item'}
            </Typography>
          ))}
        </Stack>

        {outfit.rating > 0 && (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Rating:
            </Typography>
            <Rating value={outfit.rating} readOnly size="small" />
          </Box>
        )}

        {outfit.ai_prompt && (
          <Box mt={2}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              AI Prompt:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {outfit.ai_prompt}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background:
            'radial-gradient(280px 160px at 90% -20%, rgba(255, 110, 77, 0.2), transparent 70%), linear-gradient(135deg, rgba(14, 123, 255, 0.14), rgba(0, 165, 255, 0.08))',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
          <Typography variant="h4" component="h1">
            Outfit History
          </Typography>
          <Button
            component={RouterLink}
            to="/chat"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Log New Outfit
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Track outfits over time and reuse combinations that consistently work.
        </Typography>
      </Paper>

      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3].map((idx) => (
            <Grid item key={idx} xs={12} sm={6} md={4}>
              <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Skeleton variant="rectangular" height={200} />
                <Box sx={{ p: 2 }}>
                  <Skeleton variant="text" width="60%" height={34} />
                  <Skeleton variant="rounded" height={24} width="70%" />
                  <Skeleton variant="text" width="92%" sx={{ mt: 1.5 }} />
                  <Skeleton variant="text" width="78%" />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : error ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
          <Button onClick={fetchOutfits} variant="outlined" sx={{ mt: 2 }}>
            Try Again
          </Button>
        </Paper>
      ) : outfits.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No outfit history yet
          </Typography>
          <Typography variant="body2" paragraph>
            Start logging your outfits to build your history and get better recommendations
          </Typography>
          <Button 
            component={RouterLink}
            to="/chat"
            variant="contained" 
            size="large" 
            startIcon={<AddIcon />}
          >
            Log First Outfit
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {outfits.map((outfit) => (
            <Grid item key={outfit.id} xs={12} sm={6} md={4}>
              {renderOutfitCard(outfit)}
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default OutfitHistory; 
